import {
  JackpotHitMessage,
  MessageType,
  WebSocketMessage,
} from "../models/Messages";

type MessageHandler = (data: string | object, raw?: MessageEvent) => void;
type ErrorHandler = (err: Event | Error) => void;

interface WebSocketClientOptions {
  url: string;
  protocols?: string | string[];
  json?: boolean;
  reconnect?: boolean;
  maxRetries?: number;
  initialBackoffMs?: number;
  maxBackoffMs?: number;
  debug?: boolean;
  connectTimeoutMs?: number;
}

export class WebSocketClient {
  private url: string;
  private protocols?: string | string[];
  private json: boolean;
  private reconnect: boolean;
  private maxRetries: number;
  private initialBackoffMs: number;
  private maxBackoffMs: number;
  private debug: boolean;
  private connectTimeoutMs: number;

  private ws: WebSocket | null = null;
  private retries = 0;
  private closedByUser = false;
  private connectTimer: number | null = null;
  private reconnectTimer: number | null = null;
  private isConnecting: boolean = false;

  // user callbacks
  public onOpen: (() => void) | null = null;
  public onClose: ((code?: number, reason?: string) => void) | null = null;
  public onMessage: MessageHandler | null = null;
  public onError: ErrorHandler | null = null;

  constructor(options: WebSocketClientOptions) {
    this.url = options.url;
    this.protocols = options.protocols;
    this.json = !!options.json;
    this.reconnect = options.reconnect ?? true;
    this.maxRetries = options.maxRetries ?? Infinity;
    this.initialBackoffMs = options.initialBackoffMs ?? 500;
    this.maxBackoffMs = options.maxBackoffMs ?? 30000;
    this.debug = !!options.debug;
    this.connectTimeoutMs = options.connectTimeoutMs ?? 10000;

    console.log(`Websocket client set up with url ${this.url}`);
  }

  connect(): void {
    this.closedByUser = false;

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this._connect();
  }

  private _connect(): void {
    if (this.ws || this.isConnecting) return; // already connecting/connected
    this.isConnecting = true;

    this.log("Websocket connecting to", this.url);
    try {
      this.ws = this.protocols
        ? new WebSocket(this.url, this.protocols)
        : new WebSocket(this.url);
    } catch (err) {
      this.isConnecting = false;
      this.handleError(err as Error);
      this.scheduleReconnect();
      return;
    }

    // connection timeout
    this.connectTimer = window.setTimeout(() => {
      this.log("Websocket connect timed out");
      this.cleanupSocket();
      this.handleError(new Error("WebSocket connect timeout"));
      this.scheduleReconnect();
    }, this.connectTimeoutMs);

    this.ws.onopen = () => {
      this.log("open");
      this.isConnecting = false;
      if (this.connectTimer) {
        clearTimeout(this.connectTimer);
        this.connectTimer = null;
      }
      this.retries = 0;
      this.onOpen?.();
    };

    this.ws.onmessage = (ev: MessageEvent): void => {
      let payload: string | object = ev.data;

      if (this.json) {
        try {
          payload = JSON.parse(
            typeof ev.data === "string" ? ev.data : (ev.data as any),
          );
        } catch (e) {
          this.handleError(new Error("Failed to parse JSON message"));
        }
      }

      this.onMessage?.(payload, ev);
    };

    this.ws.onerror = (ev) => {
      this.log("socket error", ev);
      this.isConnecting = false;
      this.handleError(ev);
    };

    this.ws.onclose = (ev) => {
      this.log("closed", ev.code, ev.reason);
      this.isConnecting = false;
      if (this.connectTimer) {
        clearTimeout(this.connectTimer);
        this.connectTimer = null;
      }
      const code = ev.code;
      const reason = ev.reason;
      this.cleanupSocket();
      this.onClose?.(code, reason);
      if (!this.closedByUser) this.scheduleReconnect();
    };
  }

  send(data: string | object): boolean {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      this.log("send failed: socket not open");
      return false;
    }
    try {
      const payload =
        this.json && typeof data !== "string"
          ? JSON.stringify(data)
          : String(data);
      this.ws.send(payload);
      return true;
    } catch (err) {
      this.handleError(err as Error);
      return false;
    }
  }

  close(code = 1000, reason?: string): void {
    this.closedByUser = true;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (!this.ws) return;
    try {
      this.ws.close(code, reason);
    } catch {
      this.cleanupSocket();
    }
  }

  private scheduleReconnect(): void {
    if (!this.reconnect) return;
    if (this.retries >= this.maxRetries) {
      this.log("max retries reached, will not reconnect");
      return;
    }

    this.retries += 1;

    const backoff = Math.min(
      this.initialBackoffMs * 2 ** (this.retries - 1),
      this.maxBackoffMs,
    );

    this.log(`reconnect in ${backoff}ms (attempt ${this.retries})`);

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (this.closedByUser) return;
      this._connect();
    }, backoff);
  }

  private cleanupSocket(): void {
    if (!this.ws) return;
    try {
      this.ws.onopen = null;
      this.ws.onmessage = null;
      this.ws.onclose = null;
      this.ws.onerror = null;
      if (
        this.ws.readyState === WebSocket.OPEN ||
        this.ws.readyState === WebSocket.CONNECTING
      ) {
        try {
          this.ws.close();
        } catch {}
      }
    } finally {
      this.ws = null;
      this.isConnecting = false;
    }
  }

  private handleError(err: Event | Error): void {
    try {
      this.onError?.(err);
    } catch {}
  }

  private log(...args: any[]) {
    if (this.debug) console.debug("[WebSocketClient]", ...args);
  }

  get readyState(): number {
    return this.ws ? this.ws.readyState : WebSocket.CLOSED;
  }
}
